// ADR 16.9.26r §3 — the pop-out: a second tab that keeps listening.
//
// One `BroadcastChannel`, three message kinds and no heartbeat. `hello` is
// the detached panel mounting past the gate, `bye` is every way it leaves
// (unmount, `pagehide`), and `context` is what the plan tab mirrors while it
// knows the popup is there. Every liveness gap the pair leaves closes from
// the FAB: a popup killed without `pagehide` is caught by the kept proxy's
// `closed` flag on the return press, and a reloaded plan tab (which lost the
// proxy and never persisted `detached`) re-derives the state through the
// *named* `window.open` — the browser navigates and focuses the existing
// window rather than opening another, and that window's fresh mount says
// `hello` again.
//
// `BroadcastChannel` is read off `globalThis` at call time and the channel
// is opened lazily: a tab that never pops out never constructs one, and jsdom
// ships none, so a test installs a fake with `postMessage`, `onmessage` and
// `close` and drives both ends through it.

import { useEffect } from 'react'

import type { TutorTurnContext } from '../../../wasm'

export const CHANNEL_NAME = 'lingara.tutor.context'
/** The window name: a second pop-out navigates and focuses the first. */
export const POPOUT_WINDOW_NAME = 'lingara-tutor'
export const POPOUT_URL = '/tutor?detached=1'
/**
 * 500 rather than the ADR's 480: that number is the table's
 * `list_detail_min_height`, and `breakpoints.test.ts` bans it as a bare
 * literal anywhere in `web/src`. Still compact chrome (< 600).
 */
export const POPOUT_FEATURES = 'popup,width=500,height=720'

export type PopoutMessage =
  | { kind: 'hello' }
  | { kind: 'bye' }
  | { kind: 'context'; context: TutorTurnContext | null }

/** The slice of the DOM class this file uses — what the test fake provides. */
interface ChannelLike {
  postMessage(message: unknown): void
  onmessage: ((event: { data: unknown }) => void) | null
  close(): void
}

type ChannelCtor = new (name: string) => ChannelLike

let channel: ChannelLike | null = null
const listeners = new Set<(message: PopoutMessage) => void>()
/** The `WindowProxy` the last `window.open` returned; `null` until then or when blocked. */
let proxy: Window | null = null

function isMessage(data: unknown): data is PopoutMessage {
  return typeof data === 'object' && data !== null && 'kind' in data
}

function openChannel(): ChannelLike | null {
  if (channel !== null) return channel
  const ctor = (globalThis as { BroadcastChannel?: ChannelCtor }).BroadcastChannel
  if (ctor === undefined) return null
  channel = new ctor(CHANNEL_NAME)
  channel.onmessage = (event) => {
    if (!isMessage(event.data)) return
    for (const listener of listeners) listener(event.data)
  }
  return channel
}

/** Posts, opening the channel on first use; silently nothing without one. */
export function postPopout(message: PopoutMessage): void {
  openChannel()?.postMessage(message)
}

/** Hears every message from the *other* windows on the channel. */
export function subscribePopout(listener: (message: PopoutMessage) => void): () => void {
  openChannel()
  listeners.add(listener)
  return () => {
    listeners.delete(listener)
  }
}

/**
 * `window.open` by name. `false` is a blocked popup (`null` return —
 * `useSubscriptionActions.ts:57`'s reason for refusing this call), and the
 * caller leaves the dock where it was.
 */
export function openPopout(): boolean {
  const opened = window.open(POPOUT_URL, POPOUT_WINDOW_NAME, POPOUT_FEATURES)
  if (opened === null) return false
  proxy = opened
  return true
}

/** True in the tab that made the popup — the tab whose `hello`/`bye` count (§4). */
export function holdsPopout(): boolean {
  return proxy !== null
}

/**
 * The FAB's return meaning: `true` means the popup is gone (the flag is the
 * test, since `focus()` returns nothing) and the caller folds the state back;
 * otherwise the popup is focused and the state stands.
 */
export function returnToPopout(): boolean {
  if (proxy === null || proxy.closed) {
    proxy = null
    return true
  }
  proxy.focus()
  return false
}

/** `?detached=1` — the popup's own rendering of `/tutor`. */
export function isDetachedWindow(search: string): boolean {
  return new URLSearchParams(search).get('detached') === '1'
}

/**
 * The detached panel's half of the handshake: `hello` on mount, `bye` from
 * the cleanup **and** on `pagehide` — React cleanups do not run when a tab
 * closes. Called by `TutorPanel`, not the route, because the panel exists
 * only past the gate: a popup bounced to `/plans` or `/verify` never says
 * hello, and one navigated away inside the app says bye from the unmount.
 */
export function usePopoutHandshake(active: boolean): void {
  useEffect(() => {
    if (!active) return
    postPopout({ kind: 'hello' })
    const onPageHide = () => postPopout({ kind: 'bye' })
    window.addEventListener('pagehide', onPageHide)
    return () => {
      window.removeEventListener('pagehide', onPageHide)
      postPopout({ kind: 'bye' })
    }
  }, [active])
}

/** Tests only: forget the channel and the proxy between cases. */
export function resetPopoutForTests(): void {
  channel?.close()
  channel = null
  listeners.clear()
  proxy = null
}
